<script setup lang="ts">
import { MdPreview } from 'md-editor-v3';
import 'md-editor-v3/lib/style.css';
import 'highlight.js/styles/github.css';
import 'katex/dist/katex.min.css';

defineOptions({ name: 'MarkdownPreview' });

/** Markdown 原文 */
type ModelValue = string;
/** 编辑器实例唯一 ID，多实例时需要区分 */
type EditorId = string;
/** 界面语言，支持 'zh-CN' | 'en' 等 */
type Language = string;
/** 是否隐藏代码块右上角的复制按钮 */
type NoCopy = boolean;
/** 是否禁用 KaTeX 数学公式渲染（启用需额外引入 katex 样式） */
type NoKatex = boolean;
/** 是否允许点击折叠代码块 */
type CodeFoldable = boolean;
/** 是否显示代码行号 */
type ShowCodeRowNumber = boolean;

withDefaults(
    defineProps<{
        modelValue?: ModelValue;
        editorId?: EditorId;
        language?: Language;
        noCopy?: NoCopy;
        noKatex?: NoKatex;
        codeFoldable?: CodeFoldable;
        showCodeRowNumber?: ShowCodeRowNumber;
    }>(),
    {
        modelValue: '',
        editorId: 'markdown-preview',
        language: 'zh-CN',
        noCopy: true,
        noKatex: false,
        codeFoldable: false,
        showCodeRowNumber: false,
    }
);

/** 渲染前处理 HTML：给链接加 target="_blank"，让链接在新窗口打开 */
const addLinkTarget = (html: string) =>
    html.replace(/<a(?![^>]*\starget=)/gi, '<a target="_blank" rel="noopener noreferrer"');
</script>

<template>
    <div class="markdown-preview">
        <MdPreview :editor-id="editorId" :model-value="modelValue" :language="language" :no-copy="noCopy"
            :no-katex="noKatex" :code-foldable="codeFoldable" :show-code-row-number="showCodeRowNumber" :sanitize="addLinkTarget" />
    </div>
</template>

<style scoped lang="scss">
.markdown-preview {
    width: 100%;

    :deep(.md-editor) {
        background-color: transparent !important;
    }

    :deep(.md-editor-preview-wrapper) {
        padding: 0 !important;

        .md-editor-preview {
            color: #262642;
            font-size: 14px;
            line-height: 1.65;
            word-break: break-word;

            p {
                margin: 0 0 12px;
                line-height: 26px;

                &:last-child {
                    margin-bottom: 0;
                }
            }

            img {
                max-width: 100%;
                height: auto;
                border-radius: 4px;
                margin: 16px 0;
            }

            p:has(img) {
                display: flex;
                justify-content: center;

                img {
                    margin: 0;
                }
            }

            h1,
            h2,
            h3,
            h4,
            h5,
            h6 {
                color: #262642;
                font-weight: 500;
                margin-top: 32px;
                margin-bottom: 12px;

                &:first-child {
                    margin-top: 0;
                }
            }

            h1 {
                font-size: 24px;
            }

            h2 {
                font-size: 20px;
            }

            h3 {
                font-size: 18px;
            }

            h4 {
                font-size: 16px;
            }

            a {
                color: #1e64fa;
                text-decoration: none;

                &:hover {
                    text-decoration: underline;
                }
            }

            blockquote {
                margin: 16px 0;
                padding: 8px 16px;
                border-left: 4px solid #bfdbfe;
                background: #f9fafb;
                color: #4b5563;
                border-radius: 0 4px 4px 0;
            }

            code:not(pre code) {
                font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
                font-size: 0.9em;
                background: #f3f4f6;
                padding: 2px 6px;
                border-radius: 4px;
            }

            pre {
                margin: 16px 0;
                border-radius: 8px;
                background: #f6f8fa;
                border: 1px solid #e5e7eb;
                overflow-x: auto;

                code {
                    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
                    font-size: 13px;
                    line-height: 1.55;
                }
            }

            ol,
            ul {
                padding-left: 20px;
                margin: 8px 0;

                li {
                    list-style: inherit !important;
                    margin: 4px 0;
                }
            }

            table {
                width: 100%;
                border-collapse: collapse;
                margin: 16px 0;
                font-size: 14px;

                th,
                td {
                    border: 1px solid #e5e7eb;
                    padding: 8px 12px;
                    text-align: left;
                }

                th {
                    background: #f9fafb;
                    font-weight: 500;
                }

                tr {
                    background: #fff;
                }
            }

            hr {
                margin: 24px 0;
                border: none;
                border-top: 1px solid #e5e7eb;
            }
        }
    }

    :deep(.md-editor-code) {
        background: #f6f8fa;

        .md-editor-code-lang {
            display: none;
        }
    }
}
</style>
